import { useMemo, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, errMsg } from "@/lib/api";
import { allCategories, catLabel } from "@/lib/lore";
import { useEntities, useInvalidate } from "@/lib/queries";
import { AttributeEditor } from "@/components/AttributeEditor";
import { EntityMultiSelect } from "@/components/EntityMultiSelect";
import { requestDiscovery, entityText } from "@/components/DiscoverDialog";
import { RichEditor } from "@/components/rich/RichEditor";
import { undoToast } from "@/lib/undo";
import { Maximize2, Minimize2, EyeOff } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { RecommendedFields } from "@/components/RecommendedFields";
import { missingFields } from "@/lib/templates";

export const EntityFormDialog = ({ open, onOpenChange, uid, initial, onSaved, defaultCategory = "Character" }) => {
  const invalidate = useInvalidate();
  const { data: entities = [] } = useEntities(uid);
  const [form, setForm] = useState({
    name: initial?.name || "", category: initial?.category || defaultCategory, summary: initial?.summary || "",
    content: initial?.content || "", attributes: initial?.attributes || {}, related_entity_ids: initial?.related_entity_ids || [],
    aliases: initial?.aliases || [], hidden: !!initial?.hidden,
  });
  const [busy, setBusy] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v?.target ? v.target.value : v }));
  const recFields = useMemo(() => missingFields(form.category, initial?.attributes || {}), [form.category]); // eslint-disable-line react-hooks/exhaustive-deps
  const recKeys = recFields.map((f) => f.key);
  const otherAttrs = Object.fromEntries(Object.entries(form.attributes).filter(([k]) => !recKeys.includes(k)));
  const recAttrs = Object.fromEntries(Object.entries(form.attributes).filter(([k]) => recKeys.includes(k)));

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const attributes = Object.fromEntries(Object.entries(form.attributes).filter(([k, v]) => !recKeys.includes(k) || String(v).trim()));
      const body = { ...form, attributes, aliases: form.aliases.map((a) => a.trim()).filter(Boolean) };
      if (initial?.id) delete body.content;
      const { data } = initial?.id ? await api.patch(`/entities/${initial.id}`, body) : await api.post(`/universes/${uid}/entities`, body);
      invalidate(["entities", uid], ["entity", data.id]);
      if (initial?.id) undoToast(data, "Entry updated", invalidate);
      else toast.success(`${data.name} added to the bible`);
      requestDiscovery(data, entityText(initial), entityText(form));
      onSaved?.(data);
      onOpenChange(false);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={`${expanded ? "max-w-[min(96vw,1400px)] h-[94vh]" : "max-w-4xl max-h-[92vh]"} overflow-y-auto lf-scroll transition-[max-width] duration-200`} data-testid="entity-form-dialog">
        <form onSubmit={save}>
          <DialogHeader><DialogTitle className="font-display text-3xl">{initial?.id ? `Edit ${initial.name}` : "New lore entry"}</DialogTitle></DialogHeader>
          <div className="grid gap-5 py-6">
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-[1fr_200px]">
              <div className="space-y-2"><Label>Name</Label><Input value={form.name} onChange={set("name")} required data-testid="entity-form-name" /></div>
              <div className="space-y-2">
                <Label>Category</Label>
                <Select value={form.category} onValueChange={set("category")}>
                  <SelectTrigger data-testid="entity-form-category"><SelectValue /></SelectTrigger>
                  <SelectContent>{allCategories().map((c) => <SelectItem key={c} value={c}>{catLabel(c)}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Also known as</Label>
              <Input value={form.aliases.join(", ")} onChange={(e) => set("aliases")(e.target.value.split(",").map((a) => a.trimStart()))}
                placeholder="Other names, titles or spellings, separated by commas" data-testid="entity-form-aliases" />
            </div>
            <label className="flex items-start gap-3 rounded-md border border-[#2D3442] bg-[#0F131A] px-4 py-3 cursor-pointer" data-testid="entity-form-hidden-row">
              <Switch checked={form.hidden} onCheckedChange={set("hidden")} className="mt-0.5" data-testid="entity-form-hidden-toggle" />
              <span className="text-sm">
                <span className="flex items-center gap-1.5 text-[#F3EBDD]"><EyeOff className="h-3.5 w-3.5 text-[#A78BFA]" />Hidden note</span>
                <span className="mt-0.5 block text-xs text-[#A8A090]">Only owners and editors can see it. Viewers, the public page and exports leave it out, along with its pins, events and mentions.</span>
              </span>
            </label>
            <div className="space-y-2"><Label>Summary</Label><Textarea rows={2} value={form.summary} onChange={set("summary")} data-testid="entity-form-summary" /></div>
            {initial?.id ? (
              <p className="rounded-md border border-dashed border-[#2D3442] px-3 py-2 text-xs text-[#A8A090]" data-testid="entity-form-notes-hint">Notes are edited live on the entry page, so co-authors can write together.</p>
            ) : (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Notes</Label>
                <button type="button" onClick={() => setExpanded(!expanded)} className="flex items-center gap-1 text-xs text-[#A8A090] hover:text-[#F59E0B]" data-testid="entity-form-expand-toggle">
                  {expanded ? <Minimize2 className="h-3.5 w-3.5" /> : <Maximize2 className="h-3.5 w-3.5" />}{expanded ? "Exit focus mode" : "Focus mode"}
                </button>
              </div>
              <div className="rounded-md border border-[#2D3442] bg-[#0F131A]">
                <RichEditor value={form.content} onChange={set("content")} testid="entity-form-content" minH={expanded ? "min-h-[60vh]" : "min-h-[220px]"}
                  placeholder="History, appearance, secrets… names of other entries become links." />
              </div>
              <div className="text-right text-[10px] font-mono-lf text-[#A8A090]" data-testid="entity-form-word-count">{form.content.trim() ? form.content.trim().split(/\s+/).length : 0} words</div>
            </div>
            )}
            {!expanded && <RecommendedFields fields={recFields} attributes={recAttrs} onChange={(v) => set("attributes")({ ...otherAttrs, ...v })} />}
            {!expanded && <div className="space-y-2"><Label>Other attributes</Label><AttributeEditor value={otherAttrs} onChange={(v) => set("attributes")({ ...v, ...recAttrs })} testid="entity-form-attr" /></div>}
            {!expanded && (
              <div className="space-y-2">
                <Label>Linked entries</Label>
                <EntityMultiSelect entities={entities.filter((x) => x.id !== initial?.id)} value={form.related_entity_ids} onChange={set("related_entity_ids")} testid="entity-form-related" />
              </div>
            )}
          </div>
          <DialogFooter><Button type="submit" disabled={busy} data-testid="entity-form-save">{busy ? "Saving…" : "Save entry"}</Button></DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
